// frontend/src/app/components/projects/project-corpus/import-job-card.component.ts
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ImportJob, ImportSourceType } from '../../../project-corpus/domain/types';
import {
  labelForWorkType,
  labelForSourceType,
} from '../../../project-corpus/domain/openalex-types';
import { StatusBadgeComponent } from './status-badge.component';

const SOURCE_LABELS: Record<ImportSourceType, string> = {
  OPENALEX: 'OpenAlex',
  BIBTEX: 'BibTeX',
  PDF: 'PDF upload',
  GROUP_CORPUS: 'Group corpus',
};

/**
 * Single card representing one `ImportJob` in the import-job stream. Shows
 * source icon, actor, relative timestamp, items added / needing attention,
 * the descriptor line, and a status pill. Clicking the card emits to the
 * parent so it can filter the paper list to this job.
 */
@Component({
  selector: 'app-import-job-card',
  standalone: true,
  imports: [CommonModule, StatusBadgeComponent, FormsModule],
  templateUrl: './import-job-card.component.html',
  styleUrls: ['./import-job-card.component.scss'],
})
export class ImportJobCardComponent implements OnDestroy {
  @ViewChild('titleInput') private titleInput?: ElementRef<HTMLInputElement>;
  private readonly _job = signal<ImportJob | null>(null);
  private readonly _active = signal(false);
  private focusTimeout?: ReturnType<typeof setTimeout>;

  @Input({ required: true })
  set job(value: ImportJob) {
    this._job.set(value);
  }
  get job(): ImportJob {
    return this._job() as ImportJob;
  }

  @Input()
  set active(value: boolean) {
    this._active.set(value);
  }
  get active(): boolean {
    return this._active();
  }

  /** Whether the undo button should be shown (project is in SETUP/INGESTION). */
  readonly _canUndo = signal(false);
  @Input() set canUndo(value: boolean) {
    this._canUndo.set(value);
  }
  get canUndo(): boolean {
    return this._canUndo();
  }

  /** Current user ID, used to show a "you" badge on own imports. */
  readonly _currentUserId = signal('');
  @Input() set currentUserId(value: string) {
    this._currentUserId.set(value);
  }
  get currentUserId(): string {
    return this._currentUserId();
  }

  /** PI user ID — PI can undo any import; members undo only their own. */
  readonly _piUserId = signal('');
  @Input() set piUserId(value: string) {
    this._piUserId.set(value);
  }
  get piUserId(): string {
    return this._piUserId();
  }

  @Output() cardClick = new EventEmitter<void>();
  @Output() detailClick = new EventEmitter<void>();
  @Output() titleChange = new EventEmitter<{ jobId: string; title: string }>();
  @Output() undoClick = new EventEmitter<string>();

  readonly currentJob = this._job.asReadonly();
  readonly isActive = this._active.asReadonly();
  readonly editing = signal(false);
  readonly editValue = signal('');

  readonly readyLabel = computed(() => {
    const j = this._job();
    if (!j || j.source !== 'PDF') return '';
    return `${j.papersComplete} of ${j.papersTotal} ready`;
  });

  /** Show a determinate progress bar while an OpenAlex or BibTeX import is running. */
  readonly showProgress = computed(() => {
    const j = this._job();
    return (
      !!j &&
      (j.source === 'OPENALEX' || j.source === 'BIBTEX') &&
      j.status === 'RUNNING' &&
      j.papersTotal > 0
    );
  });

  /** Fetch progress as a 0-100 percentage, clamped so it never overshoots the bar. */
  readonly progressPercent = computed(() => {
    const j = this._job();
    if (!j || j.papersTotal <= 0) return 0;
    return Math.min(100, Math.round((j.papersComplete / j.papersTotal) * 100));
  });

  readonly progressLabel = computed(() => {
    const j = this._job();
    return j ? `${j.papersComplete} / ${j.papersTotal}` : '';
  });

  readonly sourceLabel = computed(() => {
    const j = this._job();
    return j ? SOURCE_LABELS[j.source] : '';
  });

  readonly cardTitle = computed(() => {
    const j = this._job();
    if (!j) return '';
    const src = this.sourceLabel();
    if (j.title) return j.title;
    if (j.descriptor) {
      const desc = j.descriptor.replace(/^query:/, '');
      return `${src}: ${desc}`;
    }
    return src;
  });

  readonly typeFilterLine = computed(() => {
    const j = this._job();
    if (!j) return null;
    const wt = j.workTypes ?? [];
    const st = j.sourceTypes ?? [];
    const labels: string[] = [];
    if (wt.length > 0) labels.push(`Types: ${wt.map(labelForWorkType).join(', ')}`);
    if (st.length > 0) labels.push(`Venues: ${st.map(labelForSourceType).join(', ')}`);
    return labels.length > 0 ? labels.join(' · ') : null;
  });

  readonly relativeTime = computed(() => {
    const j = this._job();
    if (!j) return '';
    return formatRelative(j.startedAt);
  });

  /** Show the undo button for completed jobs when the project corpus is editable and
   *  the current user is either the PI or the user who started this import. */
  readonly showUndo = computed(() => {
    const j = this._job();
    if (!j || j.status !== 'COMPLETED' || !this._canUndo()) return false;
    const uid = this._currentUserId();
    const piId = this._piUserId();
    return uid !== '' && (uid === piId || j.startedByUserId === uid);
  });

  /** True when the current user started this import job. */
  readonly isOwn = computed(() => {
    const j = this._job();
    const uid = this._currentUserId();
    return !!j && uid !== '' && j.startedByUserId === uid;
  });

  onUndo(event: Event): void {
    event.stopPropagation();
    const j = this._job();
    if (!j) return;
    this.undoClick.emit(j.id);
  }

  startEdit(event: Event): void {
    event.stopPropagation();
    const j = this._job();
    if (!j) return;
    this.editValue.set(j.title || this.cardTitle());
    this.editing.set(true);
    clearTimeout(this.focusTimeout);
    this.focusTimeout = setTimeout(() => this.titleInput?.nativeElement.focus());
  }

  saveEdit(): void {
    const j = this._job();
    if (!j) return;
    const val = this.editValue().trim();
    if (!val) {
      this.editing.set(false);
      return;
    }
    this.titleChange.emit({ jobId: j.id, title: val });
    this.editing.set(false);
  }

  cancelEdit(): void {
    this.editing.set(false);
    this.editValue.set('');
  }

  ngOnDestroy(): void {
    clearTimeout(this.focusTimeout);
  }

  onClick(): void {
    this.cardClick.emit();
  }

  onDetail(event: Event): void {
    event.stopPropagation();
    this.detailClick.emit();
  }
}

/**
 * Lightweight relative-time formatter — keeps the dependency surface lean
 * (no `date-fns`). Returns strings like "just now", "5m ago", "2h ago",
 * "3d ago", falling back to a YYYY-MM-DD ISO date for older items.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const diffSec = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return then.toISOString().slice(0, 10);
}
